import { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2, Palette, Building2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, uploadImage, mediaUrl } from "../../lib/api";

const DEFAULT_PRIMARY = "#6D28D9";
const DEFAULT_ACCENT = "#C9A24B";

// tenant === null → create mode; otherwise edit branding of that tenant.
export default function TenantBrandingDialog({ open, onClose, tenant, onSaved }) {
  const isEdit = !!tenant;
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName(tenant?.name || "");
    const b = tenant?.branding || {};
    setLogoUrl(b.logo_url || "");
    setPrimary(b.primary || DEFAULT_PRIMARY);
    setAccent(b.accent || DEFAULT_ACCENT);
  }, [open, tenant]);

  if (!open) return null;

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setLogoUrl(url);
      toast.success("Logo cargado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo subir el logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const branding = { logo_url: logoUrl, primary, accent };
      if (isEdit) {
        await api.put(`/tenants/${tenant.id}/branding`, branding);
      } else {
        if (!name.trim()) { toast.error("El nombre es obligatorio"); setSaving(false); return; }
        await api.post("/tenants", { name: name.trim(), branding });
      }
      toast.success(isEdit ? "Marca actualizada" : "Empresa creada");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const ColorField = ({ label, value, onChange, testid }) => (
    <div className="flex-1">
      <label className="text-xs text-navy/50 mb-1.5 block">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-picker`}
          className="w-10 h-10 rounded-lg border border-navy/10 bg-white cursor-pointer p-0.5" />
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-hex`}
          className="flex-1 text-sm bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet font-mono" />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="tenant-branding-dialog">
      <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-navy/5">
          <div className="flex items-center gap-2.5">
            {isEdit ? <Palette className="w-5 h-5 text-violet" /> : <Building2 className="w-5 h-5 text-violet" />}
            <h3 className="font-medium">{isEdit ? `Marca · ${tenant.name}` : "Nueva empresa"}</h3>
          </div>
          <button onClick={onClose} data-testid="branding-close" className="text-navy/40 hover:text-navy transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {!isEdit && (
            <div>
              <label className="text-xs text-navy/50 mb-1.5 block">Nombre de la empresa</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="branding-name"
                placeholder="Ej. Acme Corp"
                className="w-full text-sm bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet" />
            </div>
          )}

          {/* Logo */}
          <div>
            <label className="text-xs text-navy/50 mb-1.5 block">Logo (blanco, fondo transparente · PNG/SVG)</label>
            <div className="flex items-center gap-4">
              <div className="w-32 h-16 rounded-lg bg-navy flex items-center justify-center overflow-hidden shrink-0" data-testid="branding-logo-preview">
                {logoUrl
                  ? <img src={mediaUrl(logoUrl)} alt="logo" className="max-h-12 max-w-[110px] object-contain" />
                  : <span className="text-cream/30 text-xs tracking-[0.25em]">OMNIA</span>}
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="branding-upload-logo"
                  className="inline-flex items-center gap-2 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors disabled:opacity-60">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {logoUrl ? "Cambiar logo" : "Subir logo"}
                </button>
                {logoUrl && (
                  <button onClick={() => setLogoUrl("")} data-testid="branding-remove-logo"
                    className="text-xs text-navy/40 hover:text-destructive transition-colors text-left">Quitar</button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/webp,image/jpeg,image/svg+xml" className="hidden" onChange={onPickLogo} data-testid="branding-file-input" />
            </div>
          </div>

          {/* Colors */}
          <div className="flex gap-4">
            <ColorField label="Color primario" value={primary} onChange={setPrimary} testid="branding-primary" />
            <ColorField label="Color de acento" value={accent} onChange={setAccent} testid="branding-accent" />
          </div>

          {/* Live preview */}
          <div className="rounded-xl border border-navy/10 p-4" data-testid="branding-preview">
            <div className="overline text-navy/40 mb-3">Vista previa</div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 rounded-full text-white text-sm px-4 py-2"
                style={{ background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)` }}>
                <Check className="w-4 h-4" /> Botón principal
              </span>
              <span className="inline-flex rounded-full text-sm px-4 py-2 border" style={{ color: primary, borderColor: primary }}>
                Secundario
              </span>
              <span className="inline-flex rounded-full text-sm px-3 py-1.5" style={{ background: `${accent}22`, color: accent }}>
                Acento
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-navy/5 bg-cream/40">
          <button onClick={onClose} className="text-sm text-navy/50 hover:text-navy px-4 py-2 transition-colors">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="branding-save"
            className="inline-flex items-center gap-2 text-sm rounded-full bg-gradient-violet text-white px-5 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {isEdit ? "Guardar marca" : "Crear empresa"}
          </button>
        </div>
      </div>
    </div>
  );
}
